import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fuel, Sparkles, Loader2, X, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// "Bijtanken?" popup — getoond in de foto-editor wanneer de credits op zijn.
// Toont de 3 pakketten en start Stripe Checkout; na betaling keert de user
// terug naar dezelfde pagina (return_path) en vult de balans automatisch aan.
export default function CreditsTopUpModal({ open, onClose }) {
    const [packs, setPacks] = useState([]);
    const [buying, setBuying] = useState(null);

    useEffect(() => {
        if (!open || packs.length > 0) return;
        api.get("/credits/packs").then((r) => setPacks(r.data.packs || [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const buy = async (packKey) => {
        setBuying(packKey);
        try {
            const { data } = await api.post("/credits/checkout", {
                package_key: packKey,
                origin_url: window.location.origin,
                return_path: window.location.pathname,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aankoop kon niet starten");
            setBuying(null);
        }
    };

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="credits-topup-modal"
        >
            <div className="bg-white rounded-md w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                        <Fuel className="h-5 w-5 text-[#E5A93D]" />
                        <h3 className="font-display font-bold text-xl text-zinc-950">Bijtanken?</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-950 transition p-1" aria-label="Sluiten" data-testid="credits-topup-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <p className="text-sm text-zinc-500 mb-5">Je credits zijn op — kies een pakket en ga meteen verder met bewerken. Je keert automatisch terug naar deze pagina.</p>
                <div className="space-y-2.5">
                    {packs.length === 0 && (
                        <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                    )}
                    {packs.map((p) => {
                        const isMost = p.key === "pro";
                        return (
                            <div
                                key={p.key}
                                data-testid={`topup-pack-${p.key}`}
                                className={`flex items-center gap-4 border rounded-sm px-4 py-3 ${isMost ? "border-[#E5A93D]/50 ring-1 ring-[#E5A93D]/20 bg-[#E5A93D]/[0.04]" : "border-zinc-200"}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-display font-bold text-zinc-950">{p.label}</span>
                                        {isMost && <span className="text-[9px] uppercase tracking-wider bg-[#E5A93D] text-black font-bold rounded-sm px-1.5 py-0.5">Meest gekozen</span>}
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        <span className="font-semibold text-zinc-800 tabular-nums">{p.credits}</span> credits · €{p.per_credit.toFixed(2)}/credit
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-bold text-lg tabular-nums" style={{ color: isMost ? "#E5A93D" : "#09090B" }}>€{p.price_eur.toFixed(0)}</div>
                                    <div className="text-[9px] uppercase tracking-wider text-zinc-400">excl. BTW</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => buy(p.key)}
                                    disabled={!!buying}
                                    data-testid={`topup-buy-${p.key}`}
                                    className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-xs font-bold transition active:scale-95 disabled:opacity-60 ${isMost ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black" : "bg-zinc-950 hover:bg-zinc-800 text-white"}`}
                                >
                                    {buying === p.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                    Koop
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-4 flex items-center gap-4 text-[11px] text-zinc-400">
                    <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> Direct beschikbaar</span>
                    <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> Vervallen nooit</span>
                    <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> Factuur inbegrepen</span>
                </div>
            </div>
        </div>
    );
}

// Onzichtbare watcher: vangt de Stripe-redirect (?purchase=success&session_id=)
// op ELKE pagina op, pollt tot betaald en ververst de credits-balans.
export function CreditPurchaseReturnWatcher() {
    const { refreshUser } = useAuth();
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        const purchase = params.get("purchase");
        const stripParams = () => {
            params.delete("purchase");
            params.delete("session_id");
            const qs = params.toString();
            window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
        };
        if (purchase === "cancelled") {
            toast.info("Aankoop geannuleerd");
            stripParams();
            return;
        }
        if (purchase !== "success" || !sessionId) return;
        let attempts = 0;
        const verifyToastId = toast.loading("Betaling verifiëren…");
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/credits/checkout/status/${sessionId}`);
                if (data.payment_status === "paid" || data.credits_added) {
                    toast.dismiss(verifyToastId);
                    toast.success(`Betaling gelukt! ${data.credits} credits toegevoegd — je kan meteen verder bewerken. ✨`, { duration: 8000 });
                    refreshUser?.();
                    stripParams();
                    return;
                }
                if (data.status === "expired") {
                    toast.dismiss(verifyToastId);
                    toast.error("Betaalsessie verlopen");
                    stripParams();
                    return;
                }
                if (attempts < 12) setTimeout(poll, 2500);
                else {
                    toast.dismiss(verifyToastId);
                    toast.info("Betaling wordt verwerkt — check zo dadelijk je balans");
                    stripParams();
                }
            } catch {
                if (attempts < 12) setTimeout(poll, 3000);
                else toast.dismiss(verifyToastId);
            }
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
